import { MouseEvent as ReactMouseEvent, useEffect, useMemo, useRef, useState } from "react"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import type { EventClickArg } from "@fullcalendar/core"
import { startOfMonth, endOfMonth, sub, add, format, isSameDay, parseISO } from "date-fns"
import { CircularProgress } from "@mui/material"

import { useFilteredActivitiesQuery } from "@/hooks/api/useActivityQuery"
import { useActivityDataListQuery } from "@/hooks/api/useDataQuery"
import { buildFilteredActivitiesParams, getActivityColor } from "@/app/activity/list/utils"
import { createActivityCommonStore, createActivityStore } from "@/app/activity/store"
import type { Activity } from "@/types/activity"

import ActivityDayPopover from "@/app/activity/list/activityDayPopover/ActivityDayPopover"
import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"

import "./activityMonthView.scss"

type PopoverState = {
  open: boolean
  anchorPosition: { top: number; left: number } | null
  date: Date | null
  activities: Activity[]
}

type DetailState = {
  open: boolean
  anchorEl: HTMLElement | null
  activity: Activity | null
}

const initialPopoverState: PopoverState = {
  open: false,
  anchorPosition: null,
  date: null,
  activities: [],
}

const initialDetailState: DetailState = {
  open: false,
  anchorEl: null,
  activity: null,
}

export default function ActivityMonthView() {
  const calendarRef = useRef<FullCalendar>(null)

  const { currentDate, selectedDate, filters, setSelectedDate } = createActivityCommonStore(
    (state) => ({
      currentDate: state.currentDate,
      selectedDate: state.selectedDate,
      filters: state.filters,
      setSelectedDate: state.setSelectedDate,
    }),
  )

  const { setTempActivity, setActivity, setUpsertMode } = createActivityStore((state) => ({
    setTempActivity: state.setTempActivity,
    setActivity: state.setActivity,
    setUpsertMode: state.setUpsertMode,
  }))

  const handleEdit = (activity: Activity) => {
    setActivity(activity)
    setTempActivity(activity)
    setUpsertMode(true)
  }

  const dateStart = startOfMonth(currentDate)
  const dateEnd = endOfMonth(currentDate)

  // viewport 외곽 일주일까지 미리 fetch (인접 달 셀에 표시되는 데이터 보강).
  // type/season-week/match-day/player 4종 필터는 /activities/filtered 가 백엔드에서 처리.
  const { data: activityList, isLoading } = useFilteredActivitiesQuery(
    buildFilteredActivitiesParams(
      filters,
      sub(dateStart, { days: 7 }).valueOf(),
      add(dateEnd, { days: 7 }).valueOf(),
    ),
  )

  const filteredActivities = useMemo<Activity[]>(
    () => (activityList ?? []) as unknown as Activity[],
    [activityList],
  )

  // 액티비티에 매칭되지 않은 업로드 데이터 구간 — WeekView 와 동일 소스(/data).
  // 이 구간이 빠지면 데이터만 있고 액티비티 없는 날짜에 데이터 아이콘이 안 떴음.
  const { data: dataRanges } = useActivityDataListQuery({
    start: sub(dateStart, { days: 7 }).valueOf(),
    end: add(dateEnd, { days: 7 }).valueOf(),
  })

  const events = useMemo(() => {
    return filteredActivities
      .filter((a) => !!a.dateStart && !Number.isNaN(new Date(a.dateStart).getTime()))
      .map((activity) => ({
        id: String(activity.idfActivity),
        title: activity.name,
        start: activity.dateStart,
        end: activity.dateFinish || activity.dateStart,
        color: getActivityColor(activity.type),
        extendedProps: { activity },
      }))
  }, [filteredActivities])

  // 데이터 아이콘은 "그 날짜에 업로드된 데이터가 있는가" 만 의미 — 액티비티 유무와 무관.
  // 데이터 구간(/data)의 start/finish 양쪽 날짜 모두 포함 (자정 넘는 구간 대비).
  const datesWithData = useMemo(() => {
    const set = new Set<string>()
    for (const r of dataRanges ?? []) {
      for (const iso of [r.dateStart, r.dateFinish]) {
        if (!iso) {
          continue
        }
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) {
          continue
        }
        set.add(format(d, "yyyy-MM-dd"))
      }
    }

    return set
  }, [dataRanges])

  // store currentDate 변경 시 FullCalendar 동기화
  // FullCalendar 의 gotoDate 가 내부적으로 flushSync 를 호출 → React 19 에서 다른 렌더 사이클과 충돌하므로
  // queueMicrotask 로 현재 렌더 사이클 이후로 미룸
  useEffect(() => {
    const api = calendarRef.current?.getApi()
    if (!api) {
      return
    }
    const key = format(currentDate, "yyyy-MM")
    const viewKey = format(api.getDate(), "yyyy-MM")
    if (key !== viewKey) {
      queueMicrotask(() => api.gotoDate(currentDate))
    }
  }, [currentDate])

  const [popoverState, setPopoverState] = useState<PopoverState>(initialPopoverState)
  const [detailState, setDetailState] = useState<DetailState>(initialDetailState)

  const closePopover = () => {
    setPopoverState((s) => ({ ...s, open: false }))
  }
  const closeDetail = () => {
    setDetailState((s) => ({ ...s, open: false }))
  }
  const openDetail = (activity: Activity, anchorEl: HTMLElement) => {
    setDetailState({ open: true, anchorEl, activity })
  }

  // 컨테이너 click 위임 — 빈 셀(공란) 클릭 처리.
  // - 이벤트 클릭은 eventClick / +more 클릭은 moreLinkClick 에서 처리
  // - 시안: 날짜 칸 공란 클릭 시 selectedDate 갱신 + 해당 날짜로 New Activity 다이얼로그 발생
  const handleContainerClick = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (target.closest(".fc-event")) {
      return
    }
    if (target.closest(".fc-daygrid-more-link")) {
      return
    }

    const cellEl = target.closest<HTMLElement>(".fc-daygrid-day")
    if (!cellEl) {
      return
    }
    const dateStr = cellEl.getAttribute("data-date")
    if (!dateStr) {
      return
    }
    const clickedDate = parseISO(dateStr)
    if (Number.isNaN(clickedDate.getTime())) {
      return
    }
    setSelectedDate(clickedDate)
    // dialog 의 useEffect([activity]) 가 mount 시 tempActivity = {...activity} 로 덮어쓰므로
    // 둘 다 같은 prefill 로 set → 날짜 prefill 유지
    const prefill = {
      dateStart: clickedDate.toISOString(),
      dateFinish: clickedDate.toISOString(),
    } as Activity
    setActivity(prefill)
    setTempActivity(prefill)
    setUpsertMode(true)
  }

  const handleEventClick = (info: EventClickArg) => {
    info.jsEvent.preventDefault()
    info.jsEvent.stopPropagation()
    const activity = info.event.extendedProps.activity as Activity | undefined
    if (!activity) {
      return
    }
    const anchorEl = info.el as HTMLElement
    openDetail(activity, anchorEl)
  }

  const handlePopoverActivityClick = (activity: Activity, anchorEl: HTMLElement) => {
    closePopover()
    openDetail(activity, anchorEl)
  }

  return (
    <>
      <div className="ActivityMonthView" onClick={handleContainerClick}>
        {isLoading && (
          <div className="ActivityMonthView__loading">
            <CircularProgress size={24} />
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin]}
          initialView="dayGridMonth"
          initialDate={currentDate}
          firstDay={filters.startOnMonday ? 1 : 0}
          events={events}
          headerToolbar={false}
          height="100%"
          expandRows
          fixedWeekCount={false}
          dayMaxEvents={true}
          navLinks={false}
          displayEventTime={false}
          eventDisplay="list-item"
          // 시안: "+2 more" 대신 "2 More" 표기
          moreLinkContent={(arg) => `${arg.num} More`}
          eventClick={handleEventClick}
          moreLinkClick={(arg) => {
            // FC navigate(navLink) 차단 + 우리 좌표 기반 popover 띄움.
            // FC 내장 popover 는 SCSS visibility:hidden 으로 invisible (DOM 유지 → anchor null 에러 방지).
            arg.jsEvent.preventDefault()
            arg.jsEvent.stopPropagation()

            const targetEl = arg.jsEvent.target as HTMLElement | null
            const cellEl = targetEl?.closest<HTMLElement>(".fc-daygrid-day")
            const calEl = cellEl?.closest<HTMLElement>(".ActivityMonthView")
            const cellRect = cellEl?.getBoundingClientRect()

            const dayActivities = filteredActivities.filter((a) => {
              if (!a.dateStart) {
                return false
              }
              const d = new Date(a.dateStart)
              if (Number.isNaN(d.getTime())) {
                return false
              }

              return isSameDay(d, arg.date)
            })
            if (dayActivities.length > 0 && cellRect) {
              // 시안: 레이어는 해당 일자 영역의 시작점(좌상단)에서 출력, width 200 / max-height 640.
              // 마지막 주 또는 항목이 많아 길이가 긴 경우 캘린더 영역의 bottom 기준으로 올려 출력.
              const POPOVER_W = 200
              const estHeight = Math.min(640, 73 + dayActivities.length * 32 + 16)
              const calRect = calEl?.getBoundingClientRect()

              let top = cellRect.top
              let left = cellRect.left
              if (calRect) {
                if (top + estHeight > calRect.bottom) {
                  top = Math.max(calRect.top, calRect.bottom - estHeight)
                }
                if (left + POPOVER_W > calRect.right) {
                  left = calRect.right - POPOVER_W
                }
                left = Math.max(calRect.left, left)
              }

              setPopoverState({
                open: true,
                anchorPosition: { top, left },
                date: arg.date,
                activities: dayActivities,
              })
            }
          }}
          dayCellClassNames={(arg) => {
            const classes: string[] = []
            if (datesWithData.has(format(arg.date, "yyyy-MM-dd"))) {
              classes.push("ActivityMonthView__day--hasData")
            }
            if (isSameDay(arg.date, selectedDate)) {
              classes.push("ActivityMonthView__day--selected")
            }

            return classes
          }}
        />
      </div>
      {/* MUI Popover 는 portal 을 통해 document.body 에 렌더되므로 컨테이너 click 위임에 영향을 주지 않음 */}
      <ActivityDayPopover
        open={popoverState.open}
        anchorPosition={popoverState.anchorPosition ?? undefined}
        date={popoverState.date}
        activities={popoverState.activities}
        onClose={closePopover}
        onActivityClick={handlePopoverActivityClick}
      />
      {detailState.activity && (
        <ActivityInfoDialog
          open={detailState.open}
          anchorEl={detailState.anchorEl}
          activity={detailState.activity}
          onClose={closeDetail}
          onEdit={handleEdit}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        />
      )}
    </>
  )
}
